/**
 * Shared presentation for Role Prioritisation.
 *
 * ── Why the rating ramp is not the design system's status colours ─────────────────────────────
 * The ratings are an ORDINAL scale and are encoded as one: dark green → green → orange → red, most
 * effort to least, so AAA and C are told apart without reading the legend. That claims the three
 * hues the design system reserves for status, so status is carried structurally instead — the
 * utilisation figure stays Trusted Blue, its label is a blue Badge, and an overcommitted cluster
 * takes a coloured top rule on its card. Do not add a red "overcommitted" fill here: red already
 * means C, and the two readings would fight.
 *
 * `#0F6B34` is the one colour on this view that is not a design-system token — the system ships no
 * dark green and no ordinal ramp. Everything else comes from the Tailwind preset.
 */
import { Badge } from '@finn-auto/design-system/atoms/badge';
import { cn } from '../../../../../shared';
import type { LoadStatus, Rating } from '../../../types/rolePrioritisation';
import { hoursLabel } from '../../../utils/rolePrioritisation';

export const RATING_BG: Record<Rating, string> = {
  AAA: 'bg-[#0F6B34]',
  A: 'bg-green',
  B: 'bg-orange',
  C: 'bg-red',
};

/** Text that reads on each fill. White on the two dark ends, near-black on the two light ones. */
const RATING_FG: Record<Rating, string> = {
  AAA: 'text-white',
  A: 'text-[#10331C]',
  B: 'text-[#3D2408]',
  C: 'text-white',
};

/** The rating as a pill. The design system's Badge variants are all soft tints and none is
 *  ordinal, so this is deliberately its own component rather than a Badge variant. */
export function RatingBadge({ rating, className }: { rating: Rating; className?: string }) {
  return (
    <span
      className={cn(
        'inline-block min-w-[38px] rounded-full px-2 py-px text-center text-xs font-semibold',
        RATING_BG[rating],
        RATING_FG[rating],
        className
      )}
    >
      {rating}
    </span>
  );
}

export function ParkedBadge() {
  return (
    <span className="inline-block min-w-[38px] rounded-full border border-solid border-pewter bg-cotton px-2 py-px text-center text-xs font-semibold text-asphalt">
      parked
    </span>
  );
}

const STATUS_VARIANT: Record<LoadStatus, 'grey' | 'blue' | 'monochrome'> = {
  headroom: 'grey',
  limit: 'blue',
  over: 'monochrome',
};

export function StatusBadge({ status, label }: { status: LoadStatus; label: string }) {
  return <Badge variant={STATUS_VARIANT[status]}>{label}</Badge>;
}

export interface BarSegment {
  rating: Rating;
  hours: number;
}

/**
 * Demand by rating against a capacity marker.
 *
 * `scale` is the denominator every segment is measured against. Passing the SAME scale to several
 * bars is what makes the three cluster cards comparable by length; each bar still carries its own
 * marker, which is why the markers sit at different points.
 */
export function StackedBar({
  segments,
  committed,
  scale,
  slim,
  markerLabel,
  ariaLabel,
}: {
  segments: BarSegment[];
  committed: number;
  scale: number;
  slim?: boolean;
  markerLabel?: string;
  ariaLabel: string;
}) {
  const safeScale = scale > 0 ? scale : 1;
  const markerPct = Math.min((committed / safeScale) * 100, 100);
  const atEnd = markerPct >= 99.5;
  return (
    <div
      className={cn('relative flex rounded-md bg-cotton', slim ? 'h-[18px]' : 'h-8')}
      role="img"
      aria-label={ariaLabel}
    >
      {segments
        .filter((s) => s.hours > 0)
        .map((s, i) => (
          <div
            key={s.rating}
            className={cn(
              'h-full flex-none first:rounded-l-md last:rounded-r-md',
              i > 0 && 'border-l-2 border-solid border-white',
              RATING_BG[s.rating]
            )}
            style={{ width: `${(s.hours / safeScale) * 100}%` }}
          />
        ))}
      <div
        className="absolute -top-1.5 -bottom-1.5 z-[3] w-0.5 rounded-full bg-trustedBlue"
        style={{ left: `${markerPct}%` }}
      >
        {markerLabel ? (
          <span
            className={cn(
              'absolute -top-[19px] whitespace-nowrap text-xs font-medium tabular-nums text-trustedBlue',
              atEnd ? '-translate-x-full pr-1.5' : '-translate-x-1/2'
            )}
          >
            {markerLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** A row of figures, no prose. Tinted blue when the news is fine, red when it is not. */
export function FactStrip({
  tone,
  children,
  className,
  testId,
}: {
  tone: 'blue' | 'red';
  children: React.ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 text-sm tabular-nums',
        tone === 'red' ? 'bg-lightRed text-[#8A0E22]' : 'bg-lightBlue text-[#0B4F9E]',
        className
      )}
    >
      {children}
    </div>
  );
}

export function Fact({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex items-baseline gap-1.5">{children}</span>;
}

/** Emphasised figure inside a FactStrip — inherits the strip's tone rather than setting a colour. */
export function FactValue({ children }: { children: React.ReactNode }) {
  return <b className="font-semibold">{children}</b>;
}

export function SectionHead({
  number,
  title,
  aside,
}: {
  number: string;
  title: string;
  aside?: string;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-3">
      <span className="text-xs font-semibold text-steel">{number}</span>
      <h2 className="text-base font-semibold text-black">{title}</h2>
      {aside ? <span className="ml-auto text-xs text-iron">{aside}</span> : null}
    </div>
  );
}

export function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-lg bg-white shadow-card', className)}>
      {children}
    </div>
  );
}

/** "1 AAA · 4 A · 7 B · 24 C", or a plain phrase when nothing whole fits. */
export function AbsorbList({
  entries,
}: {
  entries: { rating: Rating; roles: number }[];
}) {
  const shown = entries.filter((e) => e.roles >= 1);
  if (!shown.length) return <span className="text-steel">nothing whole</span>;
  return (
    <>
      {shown.map((e, i) => (
        <span key={e.rating}>
          {i > 0 ? ' · ' : ''}
          <b className="font-semibold text-asphalt">{e.roles}</b> {e.rating}
        </span>
      ))}
    </>
  );
}

export function hoursText(v: number): string {
  return `${hoursLabel(v)} h`;
}

/**
 * The tab's own numeric field and dropdown.
 *
 * Native controls, and shared from here so §03's levers and §04's model editor cannot drift into
 * two type scales — which is what "the font size is weird" was: the design-system Select trigger
 * renders `body-16-regular` (16px) inside a 14px table, which also made "Core Functions" wrap onto
 * two lines in a 152px cell.
 *
 * ⚠️ This is the documented deviation from the design-system rule, and it is the same one the
 * support form already takes (see CLAUDE.md, "Layout constraints that came from real use"). The DS
 * `Input` is built for a 56px labelled form field: with no label it renders BORDERLESS at 16px, and
 * a `className` passed to it replaces its own layout and reset classes outright rather than merging
 * with them. In a dense grid of quarter-hour estimates that is not a smaller version of the atom,
 * it is a different control. Reimplementing a labelled form field would be wrong; this is not that.
 */
export const FIELD_NUM =
  'h-8 w-[68px] rounded-md border border-solid border-pewter bg-white px-2 text-right text-[13px] ' +
  'tabular-nums text-black hover:border-iron focus:outline-none focus:ring-2 focus:ring-trustedBlue';

export const FIELD_SELECT =
  'h-8 rounded-md border border-solid border-pewter bg-white px-2 text-[13px] text-black ' +
  'hover:border-iron focus:outline-none focus:ring-2 focus:ring-trustedBlue';
